import { useState, useEffect, useCallback } from 'react';
import { Disclosure, DisclosurePanel, DisclosureButton } from '@headlessui/react';
import IconSvg from 'src/assets/icons/IconSvg';
import RaffleCard from 'src/components/cards/RaffleCard';
import SortByMenu from 'src/components/dropdowns/SortByMenu';
import RaffleFilterMenu from 'src/components/dropdowns/RaffleFilterMenu';
import SearchCollectionInput from 'src/components/input/SearchCollectionInput';
import Button from 'src/components/button/Button';
import Pagination from 'src/components/common/Pagination';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { useViewport } from 'src/context/ViewportContext';
import RafflesTable from './raffle/components/RafflesTable';
import { useRaffles } from 'src/hooks/useRaffles';
import { useRaffleActions } from 'src/hooks/useRaffleActions';
import { useWallet } from 'src/hooks/useWallet';
import { useNotification } from 'src/hooks/useNotification';
import { mapSortToApiParams } from 'src/utils/sortMapping';
import {
  getRaffleDisplayName,
  getRaffleImage,
  getRaffleCreator,
  formatTicketsSold,
  satsToBtc,
} from 'src/utils/raffleHelpers';
import type { RaffleSortOption, RaffleFilterState } from 'src/types/raffleFilters';
import { DEFAULT_FILTER_STATE } from 'src/types/raffleFilters';
import type { Raffle, RaffleQueryParams } from 'src/types/raffle';

function pickRandom<T>(arr: T[], count: number): T[] {
  const copy = [...arr];
  const result: T[] = [];
  for (let i = 0; i < count && copy.length > 0; i++) {
    const idx = Math.floor(Math.random() * copy.length);
    result.push(copy.splice(idx, 1)[0]);
  }
  return result;
}

const Home = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('ordinals');
  const [activeView, setActiveView] = useState('list');
  const { isMobile } = useViewport();

  const { buyTickets, toggleFavorite } = useRaffleActions();
  const { isConnected, signMessage } = useWallet();
  const notify = useNotification();
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [buyingId, setBuyingId] = useState<string | null>(null);

  // Sort & filter state
  const [selectedSort, setSelectedSort] = useState<RaffleSortOption>('default');
  const [filterState, setFilterState] = useState<RaffleFilterState>(DEFAULT_FILTER_STATE);
  const [currentPage, setCurrentPage] = useState(1);

  // Featured raffles
  const {
    raffles: allFeaturedRaffles,
    isLoading: featuredLoading,
    fetchRaffles: fetchFeatured,
  } = useRaffles();

  // Main raffles section
  const {
    raffles: mainRaffles,
    meta: mainMeta,
    isLoading: mainLoading,
    fetchRaffles: fetchMainRaffles,
  } = useRaffles();

  // Derived: pick random featured raffles (mix of ordinals + runes)
  const [displayFeatured, setDisplayFeatured] = useState<Raffle[]>([]);

  // Fetch featured on mount
  useEffect(() => {
    fetchFeatured({ status: 'LIVE', limit: 50 });
  }, []);

  // When featured data arrives, pick random mix
  useEffect(() => {
    if (allFeaturedRaffles.length === 0) {
      setDisplayFeatured([]);
      return;
    }
    const ordinals = allFeaturedRaffles.filter(r => r.prizeType === 'ORDINALS');
    const runes = allFeaturedRaffles.filter(r => r.prizeType === 'RUNES');

    const picked: Raffle[] = [];
    picked.push(...pickRandom(ordinals, 2));
    picked.push(...pickRandom(runes, 2));

    // Fill remainder if one type has fewer than 2
    if (picked.length < 4) {
      const remaining = allFeaturedRaffles.filter(r => !picked.includes(r));
      picked.push(...pickRandom(remaining, 4 - picked.length));
    }

    setDisplayFeatured(picked);
  }, [allFeaturedRaffles]);

  // Fetch main raffles when dependencies change
  const fetchMain = useCallback(() => {
    const sortParams = mapSortToApiParams(selectedSort);
    const params: RaffleQueryParams = {
      status: 'LIVE',
      prizeType: activeTab === 'ordinals' ? 'ORDINALS' : 'RUNES',
      page: currentPage,
      limit: 10,
      ...sortParams,
    };
    if (filterState.ticketPriceMin !== null) params.ticketPriceMin = filterState.ticketPriceMin;
    if (filterState.ticketPriceMax !== null) params.ticketPriceMax = filterState.ticketPriceMax;
    if (filterState.endDateWithin) params.endsWithin = filterState.endDateWithin;

    fetchMainRaffles(params);
  }, [activeTab, selectedSort, filterState, currentPage, fetchMainRaffles]);

  useEffect(() => {
    fetchMain();
  }, [fetchMain]);

  const handleBuy = useCallback(
    async (raffleId: string, quantity: number) => {
      if (!isConnected) {
        notify('Please connect your wallet first', 'warning');
        return;
      }
      setBuyingId(raffleId);
      const sig = await signMessage(`Buy ${quantity} ticket(s) for raffle ${raffleId}`);
      if (!sig) {
        setBuyingId(null);
        return;
      }
      const success = await buyTickets(raffleId, quantity);
      setBuyingId(null);
      if (success) {
        notify('Tickets purchased successfully!', 'success');
        fetchMain();
      } else {
        notify('Failed to purchase tickets', 'error');
      }
    },
    [isConnected, signMessage, buyTickets, notify, fetchMain],
  );

  const handleToggleFavorite = useCallback(
    async (raffleId: string) => {
      if (!isConnected) {
        notify('Please connect your wallet first', 'warning');
        return;
      }
      const result = await toggleFavorite(raffleId);
      if (result) {
        setFavoritedIds(prev => {
          const next = new Set(prev);
          if (result.favorited) {
            next.add(raffleId);
          } else {
            next.delete(raffleId);
          }
          return next;
        });
      }
    },
    [isConnected, toggleFavorite, notify],
  );

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleViewClick = (view: string) => {
    setActiveView(view);
  };

  const handleSortChange = (sort: RaffleSortOption) => {
    setSelectedSort(sort);
    setCurrentPage(1);
  };

  const handleApplyFilters = (filters: RaffleFilterState) => {
    setFilterState(filters);
    setCurrentPage(1);
  };

  const tabs = [
    { key: 'ordinals', label: 'Ordinals' },
    { key: 'runes', label: 'Runes' },
  ];

  const views = [
    { key: 'grid', icon: isMobile ? 'gridS' : 'gridM' },
    { key: 'list', icon: isMobile ? 'listS' : 'listM' },
  ];

  return (
    <>
      <section className="mb-[50px]">
        <div className="flex flex-wrap gap-3 justify-between items-center mb-6 xl:mb-8">
          <h2 className="text-brand-magnolia">Featured Raffles</h2>

          <SearchCollectionInput />
        </div>

        {featuredLoading ? (
          <div className="text-center text-grayscale-400 py-10">Loading featured raffles...</div>
        ) : displayFeatured.length > 0 ? (
          <Slider
            {...{
              dots: true,
              infinite: displayFeatured.length > 4,
              speed: 500,
              slidesToShow: Math.min(4, displayFeatured.length),
              slidesToScroll: Math.min(4, displayFeatured.length),
              className: 'home-slider',
              arrows: false,
              appendDots: (dots: React.ReactNode) => <ul>{dots}</ul>,
              customPaging: () => (
                <div className="w-3 h-3 rounded-[2px] bg-grayscale-700 transition-colors duration-200 hover:bg-grayscale-600 slick-dot" />
              ),
              responsive: [
                {
                  breakpoint: 1450,
                  settings: {
                    slidesToShow: Math.min(3, displayFeatured.length),
                    slidesToScroll: Math.min(3, displayFeatured.length),
                  },
                },
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: Math.min(2, displayFeatured.length),
                    slidesToScroll: Math.min(2, displayFeatured.length),
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  },
                },
              ],
            }}
          >
            {displayFeatured.map(raffle => (
              <div key={raffle.id} className="md:px-[9px]">
                <RaffleCard
                  image={getRaffleImage(raffle)}
                  title={getRaffleDisplayName(raffle)}
                  creator={getRaffleCreator(raffle)}
                  ticketsSold={formatTicketsSold(raffle)}
                  ticketsPrice={satsToBtc(raffle.ticketPriceSats)}
                  endDate={raffle.endDate}
                  onClick={() => navigate(`/raffle/${raffle.id}`)}
                  isFavorited={favoritedIds.has(raffle.id)}
                  onToggleFavorite={() => handleToggleFavorite(raffle.id)}
                  onBuy={(qty) => handleBuy(raffle.id, qty)}
                  isBuying={buyingId === raffle.id}
                  supply={raffle.supply}
                  ticketsSoldCount={raffle.ticketsSold}
                  ticketLimitPerWallet={raffle.ticketLimitPerWallet}
                  totalTicketValue={raffle.totalTicketValue}
                />
              </div>
            ))}
          </Slider>
        ) : (
          <div className="text-center text-grayscale-400 py-10">No featured raffles available</div>
        )}
      </section>

      <section className="mb-6 xl:mb-[60px]">
        <div className="flex flex-col gap-y-3 justify-between items-center mb-3 xl:mb-8 xl:flex-row">
          <div className="flex gap-12 justify-between items-center w-full xl:w-fit">
            <h3 className="text-brand-magnolia">Raffles</h3>

            <div className="flex text-sm font-medium rounded-md xl:text-lg bg-grayscale-900 text-grayscale-500">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  className={`px-3 xl:px-4 xl:h-[42px] h-[34px] flex items-center border border-transparent transition-all duration-200 ${
                    activeTab === tab.key
                      ? '!border-brand-tangerine bg-brand-black text-brand-magnolia rounded-md'
                      : ''
                  }`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 justify-between items-center w-full xl:gap-3 xl:w-fit">
            <div className="flex rounded-md bg-grayscale-900 w-fit">
              {views.map(view => (
                <button
                  key={view.key}
                  className={`p-2.5 flex items-center justify-center transition-all duration-200 w-fit ${
                    activeView === view.key
                      ? 'bg-grayscale-800 text-brand-tangerine rounded-md'
                      : 'text-grayscale-500'
                  }`}
                  onClick={() => handleViewClick(view.key)}
                >
                  <IconSvg icon={view.icon as any} />
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-center xl:gap-3">
              <SortByMenu selectedSort={selectedSort} onSortChange={handleSortChange} />

              <RaffleFilterMenu filterState={filterState} onApplyFilters={handleApplyFilters} />

              <Button
                variant="ghost"
                size={isMobile ? 'xs' : 'sm'}
                onClick={() => navigate('/raffle')}
              >
                View All
              </Button>
            </div>
          </div>
        </div>

        {mainLoading ? (
          <div className="text-center text-grayscale-400 py-10">Loading raffles...</div>
        ) : mainRaffles.length === 0 ? (
          <div className="text-center text-grayscale-400 py-10">No raffles found</div>
        ) : (
          <>
            {activeView === 'list' && <RafflesTable raffles={mainRaffles} />}

            {activeView === 'grid' && (
              <div className="grid gap-3 xl:gap-[18px] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {mainRaffles.map(raffle => (
                  <RaffleCard
                    key={raffle.id}
                    image={getRaffleImage(raffle)}
                    title={getRaffleDisplayName(raffle)}
                    creator={getRaffleCreator(raffle)}
                    ticketsSold={formatTicketsSold(raffle)}
                    ticketsPrice={satsToBtc(raffle.ticketPriceSats)}
                    endDate={raffle.endDate}
                    onClick={() => navigate(`/raffle/${raffle.id}`)}
                    isFavorited={favoritedIds.has(raffle.id)}
                    onToggleFavorite={() => handleToggleFavorite(raffle.id)}
                    onBuy={(qty) => handleBuy(raffle.id, qty)}
                    isBuying={buyingId === raffle.id}
                    supply={raffle.supply}
                    ticketsSoldCount={raffle.ticketsSold}
                    ticketLimitPerWallet={raffle.ticketLimitPerWallet}
                    totalTicketValue={raffle.totalTicketValue}
                  />
                ))}
              </div>
            )}

            {mainMeta && mainMeta.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={mainMeta.totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          </>
        )}
      </section>

      <section className="mb-6 xl:mb-[60px]">
        <h3 className="mb-6 xl:mb-8 text-brand-magnolia">How it Works</h3>

        <div className="grid gap-3 xl:gap-[34px] grid-cols-[repeat(auto-fit,minmax(330px,1fr))] xl:grid-cols-[repeat(auto-fit,minmax(548px,1fr))]">
          <div className="bg-grayscale-800 rounded-[15px] h-[169px] xl:h-[280px] flex items-end xl:pl-[41px] xl:pb-[43px] pl-[21px] pb-[19px] relative overflow-hidden">
            <picture>
              <source srcSet="/assets/images/home/connect-wallet.png" media="(min-width: 1024px)" />

              <img
                src="/assets/images/home/connect-wallet-mobile.png"
                alt="How it Works 1"
                className="object-cover absolute top-0 left-0 w-full h-full"
              />
            </picture>

            <div className="w-[205px] xl:w-[287px] z-10">
              <h4 className="text-brand-magnolia">Connect Your Wallet</h4>

              <p className="text-sm text-grayscale-400">
                Simply log in by connecting your preferred Ordinals wallet. No separate sign-up
                required - your wallet is your account.
              </p>
            </div>
          </div>

          <div className="bg-grayscale-800 rounded-[15px] h-[169px] xl:h-[280px] flex items-end xl:pl-[41px] xl:pb-[43px] pl-[21px] pb-[19px] relative overflow-hidden">
            <div className="w-[205px] xl:w-[287px] z-10">
              <h4 className="text-brand-magnolia">Create or Join a Raffle</h4>

              <p className="text-sm text-grayscale-400">
                Create raffles by defining prize and ticket details, or join existing ones by
                purchasing tickets.
              </p>
            </div>

            <picture>
              <source srcSet="/assets/images/home/join-raffle.png" media="(min-width: 1024px)" />

              <img
                src="/assets/images/home/join-raffle-mobile.png"
                alt="How it Works 2"
                className="object-cover absolute top-0 left-0 w-full h-full"
              />
            </picture>
          </div>

          <div className="bg-grayscale-800 rounded-[15px] h-[169px] xl:h-[280px] flex items-end xl:pl-[41px] xl:pb-[62px] pl-[21px] pb-[24.5px] relative overflow-hidden">
            <div className="w-[210px] xl:w-[287px] z-10">
              <h4 className="text-brand-magnolia">Win or Distribute Prizes</h4>

              <p className="text-sm text-grayscale-400">
                Winners are selected using a fair and secure randomization process.
              </p>
            </div>

            <picture>
              <source srcSet="/assets/images/home/win-prizes.png" media="(min-width: 1024px)" />

              <img
                src="/assets/images/home/win-prizes-mobile.png"
                alt="How it Works 3"
                className="object-cover absolute top-0 left-0 w-full h-full"
              />
            </picture>
          </div>
        </div>
      </section>

      <section>
        <h3 className="mb-6 xl:text-center xl:mb-8 text-brand-magnolia">
          Frequently Asked Questions
        </h3>

        <div className="xl:max-w-[886px] xl:mx-auto bg-grayscale-800 rounded-lg p-4 xl:p-8">
          <Disclosure as="div" className="!mb-5 xl:pt-5 w-full last:!mb-0">
            <DisclosureButton className="flex justify-between items-center w-full group gap-0.5">
              <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                How do I know I won?
              </div>

              <IconSvg
                icon="arrowDownM"
                className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
              />
            </DisclosureButton>

            <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 !text-sm xl:!text-base">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi
            </DisclosurePanel>
          </Disclosure>

          <div className="w-full border-b border-grayscale-700"></div>

          <Disclosure as="div" className="!mb-5 pt-5 w-full last:!mb-0">
            <DisclosureButton className="flex justify-between items-center w-full group gap-0.5">
              <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                How do I claim my prize(s)?
              </div>

              <IconSvg
                icon="arrowDownM"
                className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
              />
            </DisclosureButton>

            <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 !text-sm xl:!text-base">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi
            </DisclosurePanel>
          </Disclosure>

          <div className="w-full border-b border-grayscale-700"></div>

          <Disclosure as="div" className="!mb-5 pt-5 w-full last:!mb-0">
            <DisclosureButton className="flex justify-between items-center w-full group gap-0.5">
              <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                What happens if a raffle does not get enough entries?
              </div>

              <IconSvg
                icon="arrowDownM"
                className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
              />
            </DisclosureButton>

            <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 !text-sm xl:!text-base">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi
            </DisclosurePanel>
          </Disclosure>

          <div className="w-full border-b border-grayscale-700"></div>

          <Disclosure as="div" className="!mb-5 pt-5 w-full last:!mb-0">
            <DisclosureButton className="flex justify-between items-center w-full group gap-0.5">
              <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                How do I claim my refund(s)?
              </div>

              <IconSvg
                icon="arrowDownM"
                className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
              />
            </DisclosureButton>

            <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 !text-sm xl:!text-base">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi
            </DisclosurePanel>
          </Disclosure>

          <div className="w-full border-b border-grayscale-700"></div>

          <Disclosure as="div" className="!mb-5 pt-5 w-full last:!mb-0">
            <DisclosureButton className="flex justify-between items-center w-full group gap-0.5">
              <div className="text-lg xl:!text-[24px] xl:!font-medium text-grayscale-200 !font-normal xl:text-grayscale-300 !text-start">
                How do I confirm a raffle was fairly drawn?
              </div>

              <IconSvg
                icon="arrowDownM"
                className="text-grayscale-300 group-data-[open]:rotate-180 transition-all duration-200"
              />
            </DisclosureButton>

            <DisclosurePanel className="!mt-2 text-grayscale-400 transition-all duration-200 !text-sm xl:!text-base">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor
              incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
              exercitation ullamco laboris nisi
            </DisclosurePanel>
          </Disclosure>
        </div>
      </section>
    </>
  );
};

export default Home;
